import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Customer } from '../types';
import { customerAPI } from '../services/api';
import { US_STATES } from '../constants/states';

const CustomerForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<Customer>({
    name: '',
    email: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    address_line3: '',
    city: '',
    state: '',
    zip_code: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isEditMode && id) {
      fetchCustomer(parseInt(id));
    }
  }, [id, isEditMode]);

  const fetchCustomer = async (customerId: number) => {
    try {
      setLoading(true);
      const response = await customerAPI.getById(customerId);
      setFormData(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch company');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      if (isEditMode && id) {
        await customerAPI.update(parseInt(id), formData);
      } else {
        await customerAPI.create(formData);
      }
      navigate('/customers');
    } catch (err) {
      setError('Failed to save company');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="card">
        <h2>{isEditMode ? 'Edit Company' : 'Add New Company'}</h2>
        
        {error && <div className="error">{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone *</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="address_line1">Address Line 1 *</label>
            <input
              type="text"
              id="address_line1"
              name="address_line1"
              value={formData.address_line1}
              onChange={handleChange}
              placeholder="Street address, P.O. box"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="address_line2">Address Line 2</label>
            <input
              type="text"
              id="address_line2"
              name="address_line2"
              value={formData.address_line2 || ''}
              onChange={handleChange}
              placeholder="Apartment, suite, unit, building, floor, etc."
            />
          </div>

          <div className="form-group">
            <label htmlFor="address_line3">Address Line 3</label>
            <input
              type="text"
              id="address_line3"
              name="address_line3"
              value={formData.address_line3 || ''}
              onChange={handleChange}
              placeholder="Additional address information"
            />
          </div>

          <div className="form-group">
            <label htmlFor="qbo_customer_id">QuickBooks Customer ID</label>
            <input
              type="text"
              id="qbo_customer_id"
              name="qbo_customer_id"
              value={formData.qbo_customer_id || ''}
              onChange={handleChange}
              placeholder="Auto-linked on first export"
            />
            <small style={{ color: '#666' }}>
              Leave blank to auto-link on first invoice export, or enter the QBO Customer ID manually.
            </small>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label htmlFor="city">City *</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="state">State *</label>
              <select
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                required
              >
                <option value="">Select a state</option>
                {US_STATES.map((state) => (
                  <option key={state.code} value={state.code}>
                    {state.code} - {state.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="zip_code">Zip Code *</label>
              <input
                type="text"
                id="zip_code"
                name="zip_code"
                value={formData.zip_code}
                onChange={handleChange}
                placeholder="12345"
                required
              />
            </div>
          </div>

          <div className="button-group">
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : isEditMode ? 'Update Company' : 'Create Company'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/customers')}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerForm;
